<template>
  <main class="my-3">
    <div v-if="loading" id="loading" class="text-center">
      <loading></loading>
    </div>
    <section v-if="!loading">
      <b-container fluid>
        <b-row class="mb-3">
          <b-col>
            <study-details :information="studySite['studyInfo']" :milestones="studyMilestones" />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col>
            <site-details :information="studySite" :milestones="siteMilestones" />
          </b-col>
        </b-row>
        <b-row v-if="canSeeForms">
          <b-col>
            <temperature-excursion />
          </b-col>
        </b-row>
        <div class="headerTitle p-3">
          Overview
        </div>
        <application-access-two
          v-if="canSeeApplicationAccess"
          class="mb-3"
          :siteId="siteId"
          :studyId="studyId"
        ></application-access-two>
        <b-row class="h-100">
          <b-col
            v-if="canSeeEnrollment"
            class="mb-3"
            cols="12"
            lg="6"
            md="6"
            sm="12"
            style="height:25rem"
            xl="4"
          >
            <study-enrollment-progress
              :siteId="siteId"
              :studyId="studyId"
            ></study-enrollment-progress>
          </b-col>
          <!-- <b-col
            v-if="canSeeApplicationAccess"
            class="mb-3"
            cols="12"
            lg="6"
            md="6"
            sm="12"
            style="height:25rem"
            xl="4"
          >
            <my-access-requests :siteId="siteId" :studyId="studyId"></my-access-requests>
          </b-col>
          <b-col
            v-if="canSeeApplicationAccess"
            class="mb-3"
            cols="12"
            lg="6"
            md="6"
            sm="12"
            style="height:25rem"
            xl="4"
          >
            <site-team-application-access
              :siteId="siteId"
              :studyId="studyId"
            ></site-team-application-access>
          </b-col> -->
        </b-row>
        <b-row v-show="lastRefresh" class="text-center mt-5">
          <b-col cols="12">
            <div class="lastRefreshText">
              Last data update: <span style="color: blue">{{ lastRefresh }}</span>
            </div>
            <div style="font-size:12px">
              Updates to data contained on this page may be delayed - please contact your Pfizer
              point of contact with questions
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>
  </main>
</template>

<script>
  import moment from 'moment/moment.js';
  import StudyDetails from './summary-components/StudyDetails.vue';
  import SiteDetails from './summary-components/SiteDetails.vue';
  import SiteTeamApplicationAccess from './summary-components/SiteTeamApplicationAccess.vue';
  import MyAccessRequests from './summary-components/MyAccessRequests.vue';
  import StudyEnrollmentProgress from './summary-components/StudyEnrollmentProgress.vue';
  import TemperatureExcursion from './summary-components/TemperatureExcursion.vue';
  import ApplicationAccessTwo from './summary-components/ApplicationAccessTwo.vue';

  export default {
    name: 'site-summary',
    components: {
      SiteDetails,
      StudyDetails,
      SiteTeamApplicationAccess,
      MyAccessRequests,
      StudyEnrollmentProgress,
      TemperatureExcursion,
      ApplicationAccessTwo,
    },
    metaInfo() {
      let metaTitle = this.$appName + this.studyId + '-' + this.siteId + ' Summary';
      return {
        title: metaTitle,
      };
    },
    props: {
      studySite: Object,
      user: Object,
      blockedModules: Array,
    },
    data() {
      return {
        loading: false,
        studyMilestones: [],
        siteMilestones: [],
        studyId: '',
        siteId: '',
        hasForms: false,
      };
    },
    watch: {
      $route() {
        this.initialize();
      },
    },
    created() {
      this.initialize();
    },
    computed: {
      canSeeEnrollment() {
        let permissions = ['access my enrollments', 'access enrollment oversight'];
        let modules = ['Enrollment'];

        let isBlockedByStudyModules = this.blockedModules.some(x => modules.includes(x));
        let isBlockedByUserPermissions = !this.user['permissions'].some(x =>
          permissions.includes(x['name']),
        );

        return !isBlockedByStudyModules && !isBlockedByUserPermissions;
      },
      canSeeApplicationAccess() {
        return (
          !this.blockedModules.includes('Application Access') &&
          this.user.permissions.some(p => p.name === 'access application access')
        );
      },
      canSeeForms() {
        return (
          !this.blockedModules.includes('Forms') &&
          this.user.permissions.some(p => p.name === 'access forms') &&
          this.hasForms
        );
      },
      lastRefresh() {
        if (!this.studySite || !this.siteMilestones) {
          return;
        }

        const enrollmentLastRefresh =
          this.studySite.siteEnrollmentData?.lastRefresh != null
            ? moment(this.studySite.siteEnrollmentData?.lastRefresh)
            : '';
        const startUpPlanLastRefresh = this.getMaxLoadTime();

        if (!enrollmentLastRefresh) {
          return startUpPlanLastRefresh;
        }

        if (!startUpPlanLastRefresh) {
          return enrollmentLastRefresh;
        }

        return moment(enrollmentLastRefresh).isSameOrAfter(startUpPlanLastRefresh)
          ? enrollmentLastRefresh
          : startUpPlanLastRefresh;
      },
    },
    methods: {
      getMaxLoadTime() {
        let lastRefreshValues = this.siteMilestones
          .map(item => item['etlLoadTime'])
          .filter(item => item);
        if (lastRefreshValues.length > 0) {
          return moment(_.max(lastRefreshValues));
        }
      },
      initialize() {
        this.studyId = this.$route.params['studyId'];
        this.siteId = this.$route.params['siteId'];
        this.getSiteStudyMilestones();
      },
      async getSiteStudyMilestones() {
        this.loading = true;
        let endpoints = [
          `/api/cordis/milestones/${this.studyId}`,
          `/api/get-site-milestones?study=${this.studyId}&site=${this.siteId}`,
          `/api/get-forms?Study=${this.studyId}&Site=${this.siteId}&email=${window['Laravel']['user_email']}`,
        ];
        Promise.allSettled(endpoints.map(endpoint => axios.get(endpoint)))
          .then(
            axios.spread((...allData) => {
              let [study, site, forms] = allData;
              if (study.status === 'fulfilled' && study.value.status !== 204) {
                this.studyMilestones = this.mapStudyMilestones(study.value.data['studyMilestones']);
              }
              if (site.status === 'fulfilled' && site.value.status !== 204) {
                this.siteMilestones = site.value.data['items'];
              }
              if (
                forms.status === 'fulfilled' &&
                forms.value.status !== 204 &&
                forms.value.data['records'].length > 0
              ) {
                this.hasForms = true;
              }
            }),
          )
          .catch(error => {
            console.error(error);
          })
          .finally(r => {
            this.loading = false;
          });
      },
      mapStudyMilestones(milestonesData) {
        return [
          {
            id: 'studyMilestone_0',
            milestoneName: 'Protocol Approved',
            plannedDate: milestonesData['plannedProtocolApproved']
              ? this.formatDate(milestonesData['plannedProtocolApproved'])
              : null,
            actualDate: milestonesData['actualProtocolApproved']
              ? this.formatDate(milestonesData['actualProtocolApproved'])
              : null,
          },
          {
            id: 'studyMilestone_1',
            milestoneName: 'First Subject First Visit (FSFV)',
            plannedDate: milestonesData['plannedFSFV']
              ? this.formatDate(milestonesData['plannedFSFV'])
              : null,
            actualDate: milestonesData['actualFSFV']
              ? this.formatDate(milestonesData['actualFSFV'])
              : null,
          },
          {
            id: 'studyMilestone_2',
            milestoneName: 'Last Subject First Visit (LSFV)',
            plannedDate: milestonesData['plannedLSFV']
              ? this.formatDate(milestonesData['plannedLSFV'])
              : null,
            actualDate: milestonesData['actualLSFV']
              ? this.formatDate(milestonesData['actualLSFV'])
              : null,
          },
          {
            id: 'studyMilestone_3',
            milestoneName: 'Last Subject Last Visit (LSLV)',
            plannedDate: milestonesData['plannedLSLV']
              ? this.formatDate(milestonesData['plannedLSLV'])
              : null,
            actualDate: milestonesData['actualLSLV']
              ? this.formatDate(milestonesData['actualLSLV'])
              : null,
          },
          {
            id: 'studyMilestone_4',
            milestoneName: 'Database Release',
            plannedDate: milestonesData['plannedDatabaseRelease']
              ? this.formatDate(milestonesData['plannedDatabaseRelease'])
              : null,
            actualDate: milestonesData['actualDatabaseRelease']
              ? this.formatDate(milestonesData['actualDatabaseRelease'])
              : null,
          },
          {
            id: 'studyMilestone_5',
            milestoneName: 'Study Report Final',
            plannedDate: milestonesData['plannedStudyReportFinal']
              ? this.formatDate(milestonesData['plannedStudyReportFinal'])
              : null,
            actualDate: milestonesData['actualStudyReportFinal']
              ? this.formatDate(milestonesData['actualStudyReportFinal'])
              : null,
          },
        ];
      },
      formatDate(milestone) {
        if (milestone) {
          return moment(milestone).format('YYYY-MM-DD');
        }
      },
    },
  };
</script>

<style scoped>
  .headerTitle {
    font-weight: 400;
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.25px;
    color: #00004e;
  }

  .lastRefreshText {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: 0.5px;
    color: #006a68;
  }
</style>
